import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LegalLinks } from '@shared/ui/Footer';
import { LEGAL_ENTITY } from '@shared/legal/entity';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';
import { useAuth } from '@shared/auth/AuthContext';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { IconDefs, IconSearch, IconChartBars, IconMoney, IconUser, IconArrowRight } from '@shared/ui/icons';
import { InstagramIcon, EmailIcon } from '@shared/support/ContactIcons';
import { SUPPORT, igLink, mailLink } from '@shared/support/supportConfig';
import type { ComponentType } from 'react';

const ROLE_PATH: Record<string, string> = {
  admin: '/admin',
  creator: '/provider',     // role is 'creator'; its app lives at /provider
  provider: '/provider',    // legacy alias (kept harmless)
  customer: '/customer',
  precustomer: '/customer', // not-yet-active → /customer → add-brand step
  field_agent: '/agent',
  public: '/',
};

/** get_public_landing_stats() — real numbers and a live 30-day sample (anon). */
interface LandingStats {
  creators: number;
  brands_30d: number;
  creators_30d: number;
  mentions_30d: number;
  top_brands: { name: string; logo: string | null; category: string | null; creators: number; posts: number }[];
  top_creators: { handle: string; name: string; avatar: string | null; followers: number | null; brands: number; posts: number }[];
}

const contactLink: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text, #dfe6f5)',
  textDecoration: 'none', fontSize: 15, fontWeight: 500,
};
const navLink: React.CSSProperties = {
  color: 'var(--muted, #9aa4bd)', textDecoration: 'none', fontSize: 14, fontWeight: 500, alignSelf: 'center',
  whiteSpace: 'nowrap',
};
const navIcon: React.CSSProperties = {
  color: 'var(--muted, #9aa4bd)', display: 'inline-flex', alignItems: 'center', alignSelf: 'center',
};

// What a business gets — one job per card, no channels we do not actually sell.
const FEATURES: { k: string; Icon: ComponentType<{ size?: number }> }[] = [
  { k: 'competitors', Icon: IconChartBars },
  { k: 'search',      Icon: IconSearch },
  { k: 'price',       Icon: IconMoney },
];

const fmtNum = (n: number | null | undefined, lang: string) =>
  n == null ? '—' : new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'ru-RU').format(n);
const fmtK = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(n);

export default function Landing() {
  const { t, i18n } = useTranslation(['public', 'common']);
  const { user, role, loading, signOut } = useAuth();
  // While auth is still resolving OR role hasn't loaded yet, don't render a
  // Dashboard link that would resolve to '/' (public) and no-op on click.
  const dashReady = !!user && !loading && role !== 'public';
  const dashTarget = dashReady ? (ROLE_PATH[role] ?? '/') : '/';

  // A signed-in user has no reason to sit on the marketing landing — send them
  // straight to their app. Public visitors keep seeing the landing.
  const navigate = useNavigate();
  useEffect(() => {
    if (dashReady && dashTarget !== '/') navigate(dashTarget, { replace: true });
  }, [dashReady, dashTarget, navigate]);

  const { data: stats } = useSupabaseQuery<LandingStats | null>(
    ['public-landing-stats'],
    async () => supabase.rpc('get_public_landing_stats'),
    { staleTime: 10 * 60 * 1000 },
  );
  const lang = i18n.language || 'ru';
  const bizSignup = user ? (dashReady ? dashTarget : '/') : '/login?mode=signup';

  return (
    <div className="adamar-shell">
      <IconDefs />
      <div className="blob-mid" />

      <a href="#main" className="skip-link">{t('common:nav.skipToContent', { defaultValue: 'Перейти к содержимому' })}</a>
      <nav className="adamar-nav reveal">
        {/* The logo is not the page heading — one h1 per page, the hero line below. */}
        <div className="logo">Ada<span>Mar</span></div>
        <div className="adamar-nav-right">
          <a href="#about" className="nav-about" style={navLink}>{t('public:landing.navAbout')}</a>
          <a href={mailLink()} aria-label="Email" style={navIcon}><EmailIcon size={18} /></a>
          <a href={igLink} target="_blank" rel="noreferrer" aria-label="Instagram" style={navIcon}><InstagramIcon size={18} /></a>
          <LanguageSwitcher />
          {user ? (
            <>
              {dashReady ? (
                <Link to={dashTarget} className="adamar-btn adamar-btn-secondary">{t('public:landing.dashboard')}</Link>
              ) : (
                <span className="adamar-btn adamar-btn-secondary" style={{ opacity: 0.5, pointerEvents: 'none' }} aria-busy="true">
                  {t('public:landing.dashboard')}…
                </span>
              )}
              <button className="adamar-btn adamar-btn-ghost" onClick={signOut}>{t('common:nav.signOut')}</button>
            </>
          ) : (
            <Link to="/login" className="adamar-btn adamar-btn-primary">{t('common:nav.signIn')}</Link>
          )}
        </div>
      </nav>

      <section id="main" tabIndex={-1} className="adamar-hero landing-hero">
        <div className="landing-hero-text">
        <span className="eyebrow reveal d-1">{t('public:landing.eyebrow')}</span>
        <h1 className="reveal d-2">
          {t('public:landing.heroTitle')}{' '}
          <span className="grad">{t('public:landing.heroTitleAccent')}</span>
        </h1>
        <p className="reveal d-3">
          {t('public:landing.heroSubtitle', { creators: fmtNum(stats?.creators ?? 1600, lang) })}
        </p>
        <div className="adamar-hero-ctas reveal d-4">
          <Link
            to={bizSignup}
            className="adamar-btn adamar-btn-primary"
            aria-disabled={user && !dashReady ? 'true' : undefined}
            style={user && !dashReady ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
          >
            {t('public:landing.ctaPrimary')}
          </Link>
          <Link to="/login?mode=signup&type=provider" className="adamar-btn adamar-btn-secondary">
            {t('public:landing.ctaSecondary')}
          </Link>
        </div>
        <p className="landing-hero-note reveal d-5">{t('public:landing.heroNote')}</p>

        {stats && (
          <div className="landing-stats reveal d-5">
            <div><b>{fmtNum(stats.creators, lang)}</b><span>{t('public:landing.stat.creators')}</span></div>
            <div><b>{fmtNum(stats.brands_30d, lang)}</b><span>{t('public:landing.stat.brands30')}</span></div>
            <div><b>{fmtNum(stats.mentions_30d, lang)}</b><span>{t('public:landing.stat.posts30')}</span></div>
          </div>
        )}
        </div>

        {/* The product itself in the first screen: real creators from the catalogue, the
            same rows a business gets after signing up — not an abstract illustration. */}
        {stats && stats.top_creators.length > 0 && (
          <div className="landing-hero-visual reveal d-3">
            <div className="landing-hero-visual-title">{t('public:landing.heroVisualTitle')}</div>
            {stats.top_creators.slice(0, 4).map((c) => (
              <Link key={c.handle} to={`/c/${c.handle}`} className="landing-creator">
                {c.avatar ? <img src={c.avatar} alt="" width={44} height={44} /> : <div className="landing-brand-ph" />}
                <div className="landing-creator-txt">
                  <div className="landing-brand-name">{c.name}</div>
                  <div className="landing-muted">
                    {t('public:landing.creatorLine', { followers: fmtK(c.followers), count: c.brands })}
                  </div>
                </div>
                <IconArrowRight size={16} />
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Live sample: the thing a business pays for, shown before signup */}
      {stats && stats.top_brands.length > 0 && (
        <section className="adamar-section">
          <h2 className="reveal">{t('public:landing.brandsTitle')}</h2>
          <p className="section-sub reveal d-1">{t('public:landing.brandsSub')}</p>
          <div className="landing-brands">
            {stats.top_brands.map((b, i) => (
              <div key={b.name} className={`landing-brand reveal d-${Math.min(i + 1, 6)}`}>
                {b.logo ? <img src={b.logo} alt="" loading="lazy" /> : <div className="landing-brand-ph" />}
                <div className="landing-brand-txt">
                  <div className="landing-brand-name">{b.name}</div>
                  {b.category && <div className="landing-muted">{b.category}</div>}
                </div>
                <div className="landing-brand-num">
                  <b>{b.creators}</b>
                  <span>{t('public:landing.brandCreators', { count: b.creators })}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="landing-cta-row">
            <Link to={bizSignup} className="adamar-btn adamar-btn-primary">
              {t('public:landing.brandsCta')} <IconArrowRight size={16} />
            </Link>
          </div>
        </section>
      )}

      {/* The first four are already in the hero — this section continues the list. */}
      {stats && stats.top_creators.length > 4 && (
        <section className="adamar-section">
          <h2 className="reveal">{t('public:landing.creatorsTitle')}</h2>
          <p className="section-sub reveal d-1">{t('public:landing.creatorsSub')}</p>
          <div className="landing-creators">
            {stats.top_creators.slice(4).map((c) => (
              <Link key={c.handle} to={`/c/${c.handle}`} className="landing-creator">
                {c.avatar ? <img src={c.avatar} alt="" loading="lazy" /> : <div className="landing-brand-ph" />}
                <div className="landing-creator-txt">
                  <div className="landing-brand-name">{c.name}</div>
                  <div className="landing-muted">
                    {t('public:landing.creatorLine', { followers: fmtK(c.followers), count: c.brands })}
                  </div>
                </div>
                <IconArrowRight size={16} />
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="adamar-section">
        <h2 className="reveal">{t('public:landing.featuresTitle')}</h2>
        <p className="section-sub reveal d-1">{t('public:landing.featuresSub')}</p>
        {/* A numbered list, not three identical icon cards. */}
        <ol className="landing-steps">
          {FEATURES.map(({ k }, i) => (
            <li key={k} className={`reveal d-${Math.min(i + 2, 6)}`}>
              <span className="landing-step-num" aria-hidden="true">{i + 1}</span>
              <div>
                <h3>{t(`public:landing.feature.${k}.title`)}</h3>
                <p>{t(`public:landing.feature.${k}.desc`)}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="adamar-section">
        <div className="landing-creator-pitch reveal">
          <div className="icon"><IconUser size={24} /></div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h3>{t('public:landing.forCreators.title')}</h3>
            <p>{t('public:landing.forCreators.desc')}</p>
          </div>
          <Link to="/login?mode=signup&type=provider" className="adamar-btn adamar-btn-secondary">
            {t('public:landing.forCreators.cta')}
          </Link>
        </div>
      </section>

      <section id="about" className="adamar-section">
        <h2 className="reveal">{t('public:landing.aboutTitle')}</h2>
        <p className="section-sub reveal d-1" style={{ maxWidth: 720, margin: '10px 0 0', lineHeight: 1.6 }}>
          {t('public:landing.aboutBody')}
        </p>
      </section>

      <section id="contacts" className="adamar-section">
        <h2 className="reveal">{t('public:landing.contactsTitle')}</h2>
        <p className="section-sub reveal d-1">{t('public:landing.contactsSub')}</p>
        <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 18 }}>
          <a href={mailLink()} style={contactLink}><EmailIcon size={20} /> {SUPPORT.email}</a>
          <a href={igLink} target="_blank" rel="noreferrer" style={contactLink}><InstagramIcon size={20} /> @{SUPPORT.instagram}</a>
        </div>
      </section>

      <footer className="adamar-footer">
        <LegalLinks />
        <span>{t('public:landing.footer.tagline')}</span>
        <span>{t('public:landing.footer.copy')}</span>
        <span>{LEGAL_ENTITY.name} · {LEGAL_ENTITY.address}</span>
      </footer>
    </div>
  );
}
